import React, { useState } from 'react';
import { AlertTriangle, CalendarClock, Loader2, X } from 'lucide-react';
import DateSelector from '../control-tower/DateSelector';
import {
  apiErrorMessage,
  executeOperationalDeliveryAction,
} from './operationalIntelligenceApi';

const CANCELLATION_REASONS = [
  ['customer_requested', 'Cliente solicitó cancelación'],
  ['no_response', 'No responde'],
  ['store_requested', 'Tienda solicitó cancelación'],
  ['duplicate_order', 'Pedido duplicado'],
  ['invalid_address', 'Dirección inválida'],
  ['other', 'Otro'],
];

function simpleDate(value) {
  if (!value) return 'Sin fecha';
  const raw = value.split('T')[0];
  const [year, month, day] = raw.split('-');
  return `${day}/${month}/${year}`;
}

export default function DeliveryActionModal({ delivery, action, sourceStoreSignal, onClose, onCompleted, onStateChanged }) {
  const isPostpone = action === 'postpone';
  const [deliveryDate, setDeliveryDate] = useState(sourceStoreSignal?.requested_date || '');
  const [showCalendar, setShowCalendar] = useState(false);
  const [reason, setReason] = useState(
    isPostpone
      ? (sourceStoreSignal?.note || 'Solicitud reportada por la tienda')
      : (sourceStoreSignal?.reason_code || '')
  );
  const [note, setNote] = useState(isPostpone ? '' : (sourceStoreSignal?.note || ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    if (isPostpone && (!deliveryDate || !reason.trim())) {
      setError('Selecciona una nueva fecha e indica el motivo de postergación.');
      return;
    }
    if (!isPostpone && !reason) {
      setError('Selecciona un motivo de cancelación.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = isPostpone
        ? { action: 'postpone', delivery_date: deliveryDate, reason: reason.trim() }
        : {
            action: 'cancel',
            reason,
            reason_label: CANCELLATION_REASONS.find(([value]) => value === reason)?.[1],
            note: note.trim() || null,
          };
      if (sourceStoreSignal?.signal_id) {
        payload.source_store_signal_id = sourceStoreSignal.signal_id;
      }
      const result = await executeOperationalDeliveryAction(delivery.id, payload);
      await onCompleted?.(result);
    } catch (requestError) {
      const stateChanged = requestError?.response?.data?.error === 'delivery_state_changed';
      setError(apiErrorMessage(requestError));
      if (stateChanged) onStateChanged?.();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-visible rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-brand-primary to-pink-600 px-5 py-4 text-white">
          <div className="flex items-center gap-3">
            {isPostpone ? <CalendarClock className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
            <div>
              <h3 className="font-montserrat text-lg font-extrabold">
                {isPostpone ? 'Postergar entrega' : 'Cancelar entrega'}
              </h3>
              <p className="text-xs text-white/80">Acción individual sobre DeliveryPoint #{delivery.id}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-white/15" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
            <div className="col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Destinatario</p>
              <p className="font-bold text-slate-900">{delivery.recipient_name || 'Sin nombre'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tienda</p>
              <p className="font-medium text-slate-800">{delivery.seller_username || 'Sin tienda'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fecha</p>
              <p className="font-medium text-slate-800">{simpleDate(delivery.delivery_date)}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Estado actual</p>
              <p className="font-bold uppercase text-brand-primary">{delivery.status}</p>
            </div>
          </div>

          {isPostpone ? (
            <>
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-800">Nueva fecha de entrega</label>
                <DateSelector
                  selectedDate={deliveryDate}
                  onDateChange={setDeliveryDate}
                  showCalendar={showCalendar}
                  onToggleCalendar={() => setShowCalendar((value) => !value)}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-800">Motivo de postergación</label>
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Ej. Cliente solicitó una nueva fecha"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-800">Motivo de cancelación</label>
                <select
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                >
                  <option value="">Selecciona un motivo</option>
                  {CANCELLATION_REASONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-800">
                  Nota <span className="font-normal text-slate-400">(opcional)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Contexto adicional para auditoría"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
              </div>
              <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Esta acción cambiará el estado a cancelado y quedará registrada en el historial operativo.
              </div>
            </>
          )}

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}

          <div className="flex gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 rounded-xl bg-slate-100 px-4 py-2.5 font-bold text-slate-700 hover:bg-slate-200 disabled:opacity-60"
            >
              Volver
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-bold text-white disabled:opacity-60 ${
                isPostpone ? 'bg-brand-primary hover:bg-brand-primary/90' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? 'Procesando...' : isPostpone ? 'CONFIRMAR POSTERGACIÓN' : 'CONFIRMAR CANCELACIÓN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
